/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import { v4 as uuid_v4 } from "uuid";

import { IFormValues } from "./IFormValues";
import { IFormData } from "./IFormData";
import { IFormElementFactory } from "./IFormElementFactory";
import { EventEmitter } from "../EventEmitter";
import { FormElement } from "./FormElement";
import { FormFieldText, FieldText } from "./elements/FormFieldText";
import { IFormExpressionFn } from "./IFormExpressionFn";

/**
 * Form model descriptor
 */
export interface IFormModelDescriptor {
	/** Unique form type ID */
	id: string;
	/** Form version */
	version: string;
	/** Form (document) title */
	title: string;
	/** Form icon */
	icon: string;
	/** If to display from in a menu */
	showInMenu: boolean;
}

/**
 * Form model configuration
 */
export interface IFormModelConfig extends IFormModelDescriptor {
	/** Content elements */
	content?: Array<IFormElementFactory>;
}

// Event name type
export type TFormModelEventName = "update"|"change";

/**
 * Form model
 */
export class FormModel extends EventEmitter<TFormModelEventName> {

	/** Unique form fill ID */
	protected id: string;

	/** Document name */
	protected name: FormFieldText;

	/** Header document title */
	protected headerTitle: FormFieldText;

	/** Form values */
	protected values: IFormValues = {};

	/** Content elements */
	protected content: Array<FormElement<any>> = [];

	/** Tagged elements */
	protected taggedElements: { [K: string]: Array<FormElement<any>> } = {};

	/** If document was modified */
	protected modified: boolean = false;

	/**
	 * List of expressions to compute
	 */
	protected expressionList: Array<{
		fn: () => void;
		order: number;
		insertOrder: number;
	}>;

	protected lastExpressionId: number = 0;

	/**
	 * Model constructor
	 *
	 * @param config Configuration
	 */
	public constructor(protected config: IFormModelConfig) {

		super();

		// Init name & header title
		this.name = FieldText({
			id: "_documentName",
			label: "Název dokumentu",
			required: true,
			value: "Nový dokument"
		})(this, null);

		this.headerTitle = FieldText({
			id: "_documentHeaderTitle",
			label: "Hlavička dokumentu",
			required: true,
			value: config.title,
			width: "2/5",
			align: "right",
			labelAlign: "right"
		})(this, null);

		// Generate ID
		this.id = uuid_v4();

		// Init content components
		for (let i = 0; i < config.content.length; i++) {
			this.content.push(
				config.content[i](this, null)
			);
		}

	}

	/**
	 * Returns form config
	 */
	public getConfig() {

		return this.config;

	}

	/**
	 * Assigns an element to a tag
	 *
	 * @param tag Tag name
	 * @param element Element instance
	 */
	public assignTag(tag: string, element: FormElement<any>) {

		if (!this.taggedElements[tag]) {
			this.taggedElements[tag] = [];
		}

		this.taggedElements[tag].push(element);

	}

	/**
	 * Unassign an element from a tag
	 *
	 * @param tag Tag name
	 * @param element Element instance
	 */
	public unassignTag(tag: string, element: FormElement<any>) {

		if (!this.taggedElements[tag]) {
			return;
		}

		const i = this.taggedElements[tag].indexOf(element);

		if (i < 0) {
			return;
		}

		this.taggedElements[tag].splice(i, 1);

	}

	/**
	 * Returns a list of tagged elements
	 *
	 * @param tag Tag name
	 */
	public getElementByTag<TElement extends FormElement<any> = FormElement<any>>(tag: string) {

		return (this.taggedElements[tag] || []) as Array<TElement>;

	}

	/**
	 * Sets the form value
	 *
	 * @param id Value ID
	 * @param value Value
	 * @param update If to update model
	 */
	public set(id: string, value: any, update: boolean = true) {

		this.values[id] = value;

		if (update) {
			this.update();

			if (!this.modified) {
				this.modified = true;
				this.emit("update");
			}
		}

		this.emit("change");

	}

	/**
	 * Removes the form value
	 *
	 * @param id Value ID
	 */
	public unset(id: string) {

		delete this.values[id];

	}

	/**
	 * Returns the value
	 *
	 * @param id Value ID
	 */
	public get(id: string) {

		return this.values[id] !== undefined ? this.values[id] : null;

	}

	/**
	 * Returns the document name
	 */
	public getName() {

		return this.get("_documentName");

	}

	/**
	 * Returns document name element
	 */
	public getNameField() {

		return this.name;

	}

	/**
	 * Returns document name element
	 */
	public getHeaderTitleField() {

		return this.headerTitle;

	}

	/**
	 * Returns the form ID
	 */
	public getId() {

		return this.id;

	}

	/**
	 * Returns if a form was modified
	 */
	public isModified() {

		return this.modified;

	}

	/**
	 * Sets the modified state
	 *
	 * @param modified Modified state
	 */
	public setModified(modified: boolean = true) {

		this.modified = modified;
		this.emit("update");

	}

	/**
	 * Sets from data and updates model
	 *
	 * @param data Data
	 * @param update If to update model
	 */
	public setData(data: IFormData, update: boolean = true) {

		if (data.id) {
			this.id = data.id;
		}

		if (data.values) {
			this.values = data.values;
		}

		if (data.name) {
			this.values["_documentName"] = data.name;
		}

		if (data.headerTitle) {
			this.values["_documentHeaderTitle"] = data.headerTitle;
		} else {
			this.values["_documentHeaderTitle"] = this.config.title;
		}

		this.modified = false;

		if (update) {
			this.update(true);
		}

		this.emit("update");
		this.emit("change");

	}

	/**
	 * Returns form data
	 */
	public getData(): IFormData {

		return {
			formId: this.config.id,
			formVersion: this.config.version,
			id: this.id,
			name: this.values["_documentName"],
			headerTitle: this.values["_documentHeaderTitle"] || this.config.title,
			values: this.values
		};

	}

	/**
	 * Adds an expression for late computation - INTERNAL
	 *
	 * @param fn Expression function
	 * @param order Execution order (optional)
	 */
	public _addExpression(fn: () => void, order: number = 0) {

		this.expressionList.push({
			fn: fn,
			order: order,
			insertOrder: this.lastExpressionId++
		});

	}

	/**
	 * Updates the model
	 *
	 * @param resetState If to reset state
	 */
	public update(resetState: boolean = false) {

		// console.log("UPDATE", this.values);

		this.lastExpressionId = 0;
		this.expressionList = [];

		this.name.update(resetState);
		this.headerTitle.update(resetState);

		// Call update on all content elements
		for (let i = 0; i < this.content.length; i++) {
			this.content[i].update(resetState);
		}

		// Process late update (for computations)
		this.expressionList.sort((a, b) => {
			return (a.order > b.order ? 1 : a.order < b.order ? - 1 : (
				a.insertOrder > b.insertOrder ? 1 : a.insertOrder < b.insertOrder ? -1 : 0
			))
		});

		for (let i = 0; i < this.expressionList.length; i++) {
			this.expressionList[i].fn();
		}

	}

	/**
	 * Returns element state
	 */
	public getState() {

		return {
			title: this.config.title,
			version: this.config.version,
			id: this.id,
			name: this.values["_documentName"],
			headerTitle: this.values["_documentHeaderTitle"],
			content: this.content,
			modified: this.modified
		}

	}

}

/**
 * Declares section group
 *
 * @param config Config
 * @param content Content elements
 */
export function Form(
	config: IFormModelConfig,
	content: Array<IFormElementFactory<any>> = []
) {

	const _config = {
		content: content,
		...config
	};

	return () => new FormModel(_config);

}
